import React from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  getFacilityTypeSupplementLine,
  getHealthcareFacilityTypeTitle,
  mergeFacilityDetailsForTaxonomy,
  type FacilityTypeMetaInput,
} from "@utils/facilityUtils";
import {
  getAviationProfileLabel,
  getAviationSourceSummary,
} from "@utils/aviationFacilityProfile";

export type FacilityTypeValueStackProps = {
  facility: FacilityTypeMetaInput | Record<string, unknown> | null | undefined;
  /** When true (default), runs {@link mergeFacilityDetailsForTaxonomy} first. */
  mergeDetails?: boolean;
  /** Single-line mode for dense headers (e.g. Facility Network top bar). */
  compact?: boolean;
  sxPrimary?: SxProps<Theme>;
  sxSecondary?: SxProps<Theme>;
};

const defaultPrimarySx: SxProps<Theme> = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#262626",
  lineHeight: 1.35,
  wordBreak: "break-word",
  maxWidth: "100%",
};

const defaultSecondarySx: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 400,
  color: "#5D6B72",
  lineHeight: 1.35,
  wordBreak: "break-word",
  maxWidth: "100%",
};

/**
 * Primary facility-type label plus secondary line (NUCC taxonomy for healthcare,
 * aviation source summary for aviation).
 */
export function FacilityTypeValueStack({
  facility,
  mergeDetails = true,
  compact = false,
  sxPrimary,
  sxSecondary,
}: FacilityTypeValueStackProps) {
  const merged = (
    mergeDetails
      ? mergeFacilityDetailsForTaxonomy(facility as Record<string, unknown>)
      : ((facility || {}) as Record<string, unknown>)
  ) as FacilityTypeMetaInput & Record<string, unknown>;

  const isAviation =
    String(merged.vertical || "").toLowerCase() === "aviation";

  const primary = isAviation
    ? getAviationProfileLabel(merged)
    : getHealthcareFacilityTypeTitle(merged);

  const secondary = isAviation
    ? getAviationSourceSummary(merged) || "—"
    : getFacilityTypeSupplementLine(merged);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        minWidth: 0,
      }}
    >
      <Typography
        sx={
          [
            defaultPrimarySx,
            compact && {
              fontSize: "13px",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            },
            ...(sxPrimary ? [sxPrimary] : []),
          ] as SxProps<Theme>
        }
      >
        {primary}
      </Typography>
      {!compact ? (
        <Typography
          sx={
            [
              defaultSecondarySx,
              ...(sxSecondary ? [sxSecondary] : []),
            ] as SxProps<Theme>
          }
        >
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

export default FacilityTypeValueStack;
